interface GitFlowDiagramProps {
  lang: string;
}

function Arrow() {
  return (
    <div className="flex items-center justify-center text-text-muted select-none" aria-hidden="true">
      <span className="hidden sm:block text-lg">{'→'}</span>
      <span className="block sm:hidden text-lg rotate-90">{'→'}</span>
    </div>
  );
}

function BranchNode({
  label,
  sublabel,
  borderColor,
  mono = true,
}: {
  label: string;
  sublabel?: string;
  borderColor: string;
  mono?: boolean;
}) {
  return (
    <div
      className={`flex flex-col items-center justify-center rounded border-2 ${borderColor} bg-bg-primary px-3 py-2 min-w-[7rem] text-center`}
    >
      <span className={`text-sm text-text-primary leading-snug ${mono ? 'font-mono' : 'font-sans'}`}>
        {label}
      </span>
      {sublabel && (
        <span className="mt-0.5 font-mono text-xs text-text-muted">{sublabel}</span>
      )}
    </div>
  );
}

function WorkflowBadge({ file, trigger }: { file: string; trigger: string }) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className="font-mono text-xs text-accent">{file}</span>
      <span className="text-xs text-text-muted">{trigger}</span>
    </div>
  );
}

function DownArrow() {
  return (
    <div className="flex justify-center text-text-muted select-none" aria-hidden="true">
      <span className="text-lg">{'↓'}</span>
    </div>
  );
}

function UpArrow() {
  return (
    <div className="flex justify-center text-text-muted select-none" aria-hidden="true">
      <span className="text-lg">{'↑'}</span>
    </div>
  );
}

export function GitFlowDiagram({ lang }: GitFlowDiagramProps) {
  const en = lang === 'en';

  return (
    <div className="bg-bg-surface border border-border rounded p-6 space-y-6 overflow-x-auto">
      {/* Main flow label */}
      <p className="font-mono text-xs text-text-muted">
        {en ? '// Standard flow' : '// Flujo estándar'}
      </p>

      {/* Main horizontal flow */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-2 min-w-max sm:min-w-0">
        <BranchNode label="feature/*" borderColor="border-purple-500" />
        <Arrow />
        <div className="flex items-center justify-center w-9 h-9 rounded-full border border-border bg-bg-primary text-text-muted font-mono text-xs self-center">
          PR
        </div>
        <Arrow />
        <div className="flex flex-col items-center gap-2">
          <BranchNode label="dev" borderColor="border-yellow-500" />
          <DownArrow />
          <WorkflowBadge file="deploy-dev.yml" trigger={en ? 'auto on push' : 'auto al push'} />
        </div>
        <Arrow />
        <div className="flex items-center justify-center w-9 h-9 rounded-full border border-border bg-bg-primary text-text-muted font-mono text-xs self-center">
          PR
        </div>
        <Arrow />
        <BranchNode label="main" borderColor="border-green-500" />
        <Arrow />
        <div className="flex flex-col items-center gap-2">
          <BranchNode label="tag prd-x.x.x" borderColor="border-accent" />
          <DownArrow />
          <WorkflowBadge file="deploy-prd.yml" trigger={en ? 'auto on tag' : 'auto al tag'} />
        </div>
        <Arrow />
        <BranchNode
          label={en ? 'Production' : 'Producción'}
          borderColor="border-accent"
          mono={false}
        />
      </div>

      {/* Separator */}
      <div className="border-t border-border" />

      {/* Hotfix flow label */}
      <p className="font-mono text-xs text-text-muted">
        {en ? '// Hotfix flow (from production)' : '// Flujo de hotfix (desde producción)'}
      </p>

      {/* Hotfix flow */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-2 min-w-max sm:min-w-0">
        <BranchNode
          label="main"
          sublabel={en ? '(production code)' : '(código en prd)'}
          borderColor="border-green-500"
        />
        <Arrow />
        <BranchNode label="hotfix/*" borderColor="border-red-500" />
        <Arrow />
        <div className="flex flex-col items-center gap-2">
          <BranchNode
            label={en ? 'Quick release' : 'Release rápido'}
            sublabel="tag prd-x.x.x"
            borderColor="border-red-500"
          />
          <DownArrow />
          <WorkflowBadge file="deploy-prd.yml" trigger={en ? 'auto on tag' : 'auto al tag'} />
        </div>
        <Arrow />
        <BranchNode
          label={en ? 'Production' : 'Producción'}
          sublabel={en ? '(fixed)' : '(corregido)'}
          borderColor="border-accent"
          mono={false}
        />
      </div>

      {/* Sync back to dev */}
      <div className="flex items-center gap-3 sm:ml-[9.5rem]">
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-0.5 bg-red-500/50" />
          <span className="inline-block w-3 h-0.5 bg-red-500/50" />
          <span className="inline-block w-3 h-0.5 bg-red-500/50" />
        </div>
        <div className="flex flex-col items-center gap-1">
          <UpArrow />
          <BranchNode
            label={en ? 'merge → dev' : 'merge → dev'}
            sublabel="sync"
            borderColor="border-yellow-500"
          />
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 border-t border-border pt-4">
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-sm border-2 border-purple-500" />
          <span className="font-mono text-xs text-text-muted">feature/*</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-sm border-2 border-yellow-500" />
          <span className="font-mono text-xs text-text-muted">dev</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-sm border-2 border-green-500" />
          <span className="font-mono text-xs text-text-muted">main</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-sm border-2 border-accent" />
          <span className="font-mono text-xs text-text-muted">
            {en ? 'tag / production' : 'tag / producción'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-sm border-2 border-red-500" />
          <span className="font-mono text-xs text-text-muted">hotfix/*</span>
        </div>
      </div>
    </div>
  );
}
